/**
 * Asks for the player's name: once, the first time they start any game, and
 * again only when they choose "Change" on Home. Required, trimmed, capped at
 * NAME_MAX_LENGTH (the server's limit).
 */
import { useEffect, useState } from 'react';
import { cleanName } from '../../worker/protocol';
import { NAME_MAX_LENGTH, savePlayerName } from '../lib/playerName';
import { Sheet } from './Sheet';
import { Tibbs } from './Art';
import { unlockAudio } from '../lib/sound';
import { ensureName, nameClipUrl, prefetchNames, speakableName } from '../tibbs/names';
import { play } from '../tibbs/voicePlayer';
import { normalizeTibbsSettings, voiceGain } from '../tibbs/settings';
import { KEYS, load } from '../lib/storage';
import { isMuted } from '../lib/sound';

export function NameSheet({
  open,
  initial,
  first,
  onSave,
  onClose,
}: {
  open: boolean;
  /** The current name, when changing it. */
  initial: string;
  /** First time: "What's your name?" and Continue; otherwise Save. */
  first: boolean;
  onSave: (name: string) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [hear, setHear] = useState<'idle' | 'busy' | 'failed'>('idle');
  // Start from the current name each time the sheet opens.
  useEffect(() => {
    if (open) setDraft(initial);
  }, [open]);
  const clean = cleanName(draft);
  return (
    <Sheet open={open} onClose={onClose} title={first ? 'What’s your name?' : 'Your name'} labelledBy="name-title" className="online-sheet name-sheet">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const saved = savePlayerName(draft);
          if (saved) {
            // Tibbs learns the name now (and it is kept for offline games).
            prefetchNames([saved]);
            onSave(saved);
          }
        }}
      >
        {first && <p className="confirm-text name-why">It goes on the scorecard, here and in games with friends.</p>}
        <label className="field">
          {/* The sheet's title already asks; the label stays for screen readers. */}
          <span className="sr-only">Your name</span>
          <input
            id="player-name"
            data-autofocus
            value={draft}
            maxLength={NAME_MAX_LENGTH}
            autoComplete="nickname"
            autoCapitalize="words"
            enterKeyHint="done"
            placeholder="Sam"
            onChange={(e) => setDraft(e.target.value)}
          />
        </label>
        {clean && speakableName(clean) && (
          <button
            type="button"
            className="btn btn-ink btn-sm name-hear"
            disabled={hear === 'busy'}
            onClick={async () => {
              unlockAudio();
              setHear('busy');
              const ok = await ensureName(clean, 'plain');
              if (!ok) return setHear('failed');
              const gain = voiceGain(normalizeTibbsSettings(load(KEYS.tibbs, null)), isMuted());
              play({ steps: [{ kind: 'name', url: nameClipUrl(clean, 'plain'), seat: 0 }], gain, onCue: () => {}, onState: (st) => (st === 'done' || st === 'stopped') && setHear('idle') });
            }}
          >
            <Tibbs pose="wry" size={22} /> {hear === 'failed' ? 'Tibbs can’t reach the club right now' : 'Hear Tibbs say it'}
          </button>
        )}
        <button className="btn btn-gold btn-big wide-btn" type="submit" disabled={!clean}>
          {first ? 'Continue' : 'Save'}
        </button>
      </form>
    </Sheet>
  );
}
